import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Input,
  VStack,
  Heading,
  Card,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import { Field } from "@chakra-ui/react";

import { useAppDispatch, useAppSelector } from "../store/hooks";
import { updateProfile, getProfile } from "../store/slices/authSlice";
import type { UserUpdate } from "../types/index";
import { toaster } from "../components/ui/toaster";

export default function ProfilePage() {
  const dispatch = useAppDispatch();
  const { user, loading } = useAppSelector((state) => state.auth);

  const [formData, setFormData] = useState<UserUpdate>({
    first_name: "",
    last_name: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    country: "",
  });

  // Fetch profile on mount
  useEffect(() => {
    dispatch(getProfile());
  }, [dispatch]);

  // Update form data when user data changes
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        phone: user.phone || "",
        address: user.address || "",
        city: user.city || "",
        state: user.state || "",
        country: user.country || "",
      });
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await dispatch(updateProfile(formData)).unwrap();
      toaster.create({
        title: "Profile updated",
        type: "success",
        duration: 3000,
      });
    } catch (error: any) {
      toaster.create({
        title: "Update failed",
        description: error,
        type: "error",
        duration: 5000,
      });
    }
  };

  return (
    <Box p={6}>
      <Heading className="text-center" size="xl" mb={6}>
        Profile Settings
      </Heading>

      <Card.Root maxW="4xl" mx={"auto"} width={"100%"}>
        <Card.Body p={4}>
          <form onSubmit={handleSubmit}>
            <VStack gap={4}>
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={4}
                width="full">
                <GridItem>
                  <Field.Root>
                    <Field.Label>First Name</Field.Label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) =>
                        setFormData({ ...formData, first_name: e.target.value })
                      }
                    />
                  </Field.Root>
                </GridItem>
                <GridItem>
                  <Field.Root>
                    <Field.Label>Last Name</Field.Label>
                    <Input
                      value={formData.last_name}
                      onChange={(e) =>
                        setFormData({ ...formData, last_name: e.target.value })
                      }
                    />
                  </Field.Root>
                </GridItem>
              </Grid>

              <Field.Root width="full">
                <Field.Label>Email</Field.Label>
                <Input value={user?.email} disabled />
              </Field.Root>

              <Field.Root width="full">
                <Field.Label>Phone</Field.Label>
                <Input
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                />
              </Field.Root>

              <Field.Root width="full">
                <Field.Label>Address</Field.Label>
                <Input
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                />
              </Field.Root>

              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={4}
                width="full">
                <GridItem>
                  <Field.Root>
                    <Field.Label>City</Field.Label>
                    <Input
                      value={formData.city}
                      onChange={(e) =>
                        setFormData({ ...formData, city: e.target.value })
                      }
                    />
                  </Field.Root>
                </GridItem>
                <GridItem>
                  <Field.Root>
                    <Field.Label>State</Field.Label>
                    <Input
                      value={formData.state}
                      onChange={(e) =>
                        setFormData({ ...formData, state: e.target.value })
                      }
                    />
                  </Field.Root>
                </GridItem>
              </Grid>

              <Field.Root width="full">
                <Field.Label>Country</Field.Label>
                <Input
                  value={formData.country}
                  onChange={(e) =>
                    setFormData({ ...formData, country: e.target.value })
                  }
                />
              </Field.Root>

              <Button
                type="submit"
                colorScheme="blue"
                width="full"
                loading={loading}
                mt={4}>
                Update Profile
              </Button>
            </VStack>
          </form>
        </Card.Body>
      </Card.Root>
    </Box>
  );
}
